import type { Session } from '@supabase/supabase-js';
import {
  SUPABASE_AUTH_STORAGE_KEY,
  SUPABASE_AUTH_USER_STORAGE_KEY,
  getSupabaseClient,
} from './supabase';
import { applyTenantRequestHeaders } from './tenantRequestContext';

const SESSION_USER_KEY = 'tt_session_user';
const SESSION_STATE_KEY = 'tt_session_state';
const MOCK_USER_KEY = 'tt_mock_user';
const POST_AUTH_REDIRECT_KEY = 'tt_post_auth_redirect';
const LOGIN_FLOW_ACTIVE_KEY = 'tt_login_flow_active';
const LOGIN_AUTH_ERROR_KEY = 'tt_login_auth_error';
const LOGIN_SUCCESS_MESSAGE_KEY = 'tt_login_success_message';
const LOGOUT_TRANSITION_PENDING_KEY = 'tt_logout_transition_pending';
const SIGNUP_CHECKOUT_HANDOFF_KEY = 'tt_signup_checkout_handoff_active';
const FIRST_SESSION_ONBOARDING_REQUESTED_KEY = 'tradeproof:first-session-onboarding-requested.v1';
const WIDGET_TIMEFRAME_STORAGE_PREFIX = 'tt_widget_timeframe.';
const JOURNAL_DATE_RANGE_STORAGE_KEY = 'journal.dateRange.v1';
const AUTH_DEBUG_STORAGE_KEY = 'tt_auth_debug';
const AUTH_SESSION_REFRESH_SKEW_MS = 60_000;
const AUTH_SESSION_EXPIRED_MESSAGE = 'Your session expired. Please sign in again.';
const IDENTIFIER_SIGN_IN_ENDPOINT = '/api/auth/login-identifier';
const IDENTIFIER_SIGN_UP_ENDPOINT = '/api/auth/signup-identifier';
const USERNAME_AVAILABILITY_ENDPOINT = '/api/auth/username-availability';
const GENERIC_IDENTIFIER_SIGN_IN_FAILURE_MESSAGE = 'Invalid email, username, or password.';
export const GENERIC_IDENTIFIER_SIGN_UP_FAILURE_MESSAGE = "We couldn't create your account. Please try again.";
export const USERNAME_TAKEN_MESSAGE = 'That username is already taken.';
export const EMAIL_ALREADY_EXISTS_MESSAGE = 'An account with this email already exists. Sign in or reset your password.';
export const USERNAME_REQUIREMENTS_MESSAGE = '3-20 characters. Letters, numbers, hyphens, and underscores only.';
export const USERNAME_AVAILABLE_MESSAGE = 'Username available';
const USERNAME_TAKEN_CODE = 'username_taken';
const EMAIL_ALREADY_EXISTS_CODE = 'email_exists';
const UNEXPECTED_SIGNUP_FAILURE_CODE = 'unexpected_failure';
const INVALID_PASSWORD_CODE = 'invalid_password';
export const INVALID_PASSWORD_MESSAGE = 'At least 6 characters, including 1 uppercase and 1 number.';

const getAppBaseUrl = () => {
  try {
    const currentOrigin = String(window.location.origin || '').trim().replace(/\/+$/, '');
    if (currentOrigin) return currentOrigin;
  } catch {}

  try {
    const configured = String(import.meta.env.VITE_APP_URL || import.meta.env.VITE_SITE_URL || '').trim().replace(/\/+$/, '');
    if (configured) return configured;
  } catch {}

  return '';
};

const buildAppUrl = (pathname: string) => {
  const baseUrl = getAppBaseUrl();
  const normalizedPath = String(pathname || '/').trim().startsWith('/') ? String(pathname || '/').trim() : `/${String(pathname || '/').trim()}`;
  return `${baseUrl}${normalizedPath}`;
};

type AuthenticatedRequestSessionError = Error & {
  code?: string;
  status?: number;
};

type AuthenticatedRequestSessionOptions = {
  reason?: string;
  refreshThresholdMs?: number;
};

type SessionCommitOptions = {
  reason?: string;
  requireAuthoritativeStorage?: boolean;
};

const getSessionExpiryMs = (session: Session | null | undefined) => {
  const expiresAtSeconds = Number((session as { expires_at?: number | null } | null | undefined)?.expires_at || 0);
  if (!Number.isFinite(expiresAtSeconds) || expiresAtSeconds <= 0) {
    return null;
  }
  return expiresAtSeconds * 1000;
};

const isSessionExpired = (session: Session | null | undefined, skewMs = 0) => {
  const expiryMs = getSessionExpiryMs(session);
  if (expiryMs == null) {
    return false;
  }
  return expiryMs <= Date.now() + Math.max(0, skewMs);
};

const createAuthenticatedRequestSessionError = (
  message: string,
  code: string,
  extras?: { status?: number | null },
) => {
  const error = new Error(message) as AuthenticatedRequestSessionError;
  error.code = code;
  if (extras?.status != null) {
    error.status = extras.status;
  }
  return error;
};

const getAuthoritativeStoragePresence = () => ({
  storageKeyPresent: readStorageItem(localStorage, SUPABASE_AUTH_STORAGE_KEY) != null,
  storageUserKeyPresent: readStorageItem(localStorage, SUPABASE_AUTH_USER_STORAGE_KEY) != null,
});

type AuthDebugSnapshot = {
  href: string | null;
  origin: string | null;
  hostname: string | null;
  pathname: string | null;
  search: string | null;
  hash: string | null;
  storage: {
    supabaseAuthPresent: boolean;
    supabaseAuthUserPresent: boolean;
    sessionStatePresent: boolean;
    sessionUserPresent: boolean;
    loginFlowActive: boolean;
    postAuthRedirect: string | null;
    loginAuthErrorPresent: boolean;
    loginSuccessMessagePresent: boolean;
    logoutTransitionPending: boolean;
    signupCheckoutHandoffPresent: boolean;
    mockUserPresent: boolean;
  };
};

const readStorageItem = (storage: Storage | undefined, key: string) => {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

export function isAuthDebugEnabled() {
  try {
    if (String(import.meta.env.VITE_AUTH_DEBUG || '').trim() === 'true') {
      return true;
    }
  } catch {}

  try {
    return readStorageItem(localStorage, AUTH_DEBUG_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function getAuthDebugSnapshot(): AuthDebugSnapshot {
  try {
    return {
      href: window.location.href,
      origin: window.location.origin,
      hostname: window.location.hostname,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      storage: {
        supabaseAuthPresent: readStorageItem(localStorage, SUPABASE_AUTH_STORAGE_KEY) != null,
        supabaseAuthUserPresent: readStorageItem(localStorage, SUPABASE_AUTH_USER_STORAGE_KEY) != null,
        sessionStatePresent: readStorageItem(localStorage, SESSION_STATE_KEY) != null,
        sessionUserPresent: readStorageItem(localStorage, SESSION_USER_KEY) != null,
        loginFlowActive: readStorageItem(sessionStorage, LOGIN_FLOW_ACTIVE_KEY) === 'true',
        postAuthRedirect: readStorageItem(sessionStorage, POST_AUTH_REDIRECT_KEY),
        loginAuthErrorPresent: readStorageItem(sessionStorage, LOGIN_AUTH_ERROR_KEY) != null,
        loginSuccessMessagePresent: readStorageItem(sessionStorage, LOGIN_SUCCESS_MESSAGE_KEY) != null,
        logoutTransitionPending: readStorageItem(sessionStorage, LOGOUT_TRANSITION_PENDING_KEY) === 'true',
        signupCheckoutHandoffPresent: readStorageItem(sessionStorage, SIGNUP_CHECKOUT_HANDOFF_KEY) != null,
        mockUserPresent: readStorageItem(localStorage, MOCK_USER_KEY) != null,
      },
    };
  } catch {
    return {
      href: null,
      origin: null,
      hostname: null,
      pathname: null,
      search: null,
      hash: null,
      storage: {
        supabaseAuthPresent: false,
        supabaseAuthUserPresent: false,
        sessionStatePresent: false,
        sessionUserPresent: false,
        loginFlowActive: false,
        postAuthRedirect: null,
        loginAuthErrorPresent: false,
        loginSuccessMessagePresent: false,
        logoutTransitionPending: false,
        signupCheckoutHandoffPresent: false,
        mockUserPresent: false,
      },
    };
  }
}

export function authDebugLog(event: string, payload: Record<string, unknown> = {}) {
  if (!isAuthDebugEnabled()) return;
  try {
    console.info('[TT-AUTH-DEBUG]', event, {
      ts: Date.now(),
      ...getAuthDebugSnapshot(),
      ...payload,
    });
  } catch {}
}

export type AppIdentity = {
  uid: string;
  email: string | null;
  displayName: string | null;
  locationLabel?: string | null;
  createdAt?: string | null;
  lastLoginAt?: string | null;
  isAnonymous: boolean;
};

const clean = (value: string | undefined) => {
  if (!value) return '';
  let next = value.trim();
  if ((next.startsWith('"') && next.endsWith('"')) || (next.startsWith("'") && next.endsWith("'"))) {
    next = next.slice(1, -1);
  }
  return next;
};

const normalizeDisplayName = (value: string | undefined) => clean(value);

const isLikelyEmailAddress = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean(value));

const isInvalidCredentialError = (error: { message?: string; status?: number; code?: string } | null | undefined) => {
  const message = String(error?.message || '').trim().toLowerCase();
  const code = String(error?.code || '').trim().toLowerCase();
  const status = Number(error?.status || 0);

  return status === 401
    || code === 'invalid_login_credentials'
    || code === 'invalid_credentials'
    || message === 'invalid login credentials'
    || message === GENERIC_IDENTIFIER_SIGN_IN_FAILURE_MESSAGE.toLowerCase();
};

const isExpiredJwtError = (error: unknown) => {
  if (!error || typeof error !== 'object') return false;
  const err = error as { message?: string; status?: number; code?: string };
  const message = String(err.message || '').trim().toLowerCase();
  const code = String(err.code || '').trim().toLowerCase();
  const status = Number(err.status || 0);

  return status === 401
    || code === 'auth_session_expired'
    || code === 'auth_session_invalid'
    || code === 'auth_session_missing'
    || code === 'invalid_jwt'
    || message.includes('token is expired')
    || message.includes('invalid jwt')
    || message.includes('unable to parse or verify signature')
    || message.includes('session expired');
};

const toIdentifierSignInError = (error: { message?: string; status?: number; code?: string } | Error) => {
  if (isInvalidCredentialError(error)) {
    return new Error(GENERIC_IDENTIFIER_SIGN_IN_FAILURE_MESSAGE);
  }

  if (error instanceof Error) return error;
  return new Error(String(error?.message || 'Authentication failed. Please try again.').trim() || 'Authentication failed. Please try again.');
};

const isUsernameTakenError = (error: { message?: string; status?: number; code?: string } | null | undefined) => {
  const message = String(error?.message || '').trim().toLowerCase();
  const code = String(error?.code || '').trim().toLowerCase();

  return code === USERNAME_TAKEN_CODE
    || message === USERNAME_TAKEN_MESSAGE.toLowerCase();
};

const isEmailAlreadyExistsError = (error: { message?: string; status?: number; code?: string } | null | undefined) => {
  const message = String(error?.message || '').trim().toLowerCase();
  const code = String(error?.code || '').trim().toLowerCase();

  return code === EMAIL_ALREADY_EXISTS_CODE
    || message === EMAIL_ALREADY_EXISTS_MESSAGE.toLowerCase();
};

const isUnexpectedSignupFailure = (error: { message?: string; status?: number; code?: string } | null | undefined) => {
  const code = String(error?.code || '').trim().toLowerCase();
  const status = Number(error?.status || 0);

  return status >= 500
    || code === UNEXPECTED_SIGNUP_FAILURE_CODE;
};

const isPasswordValidationError = (error: { message?: string; status?: number; code?: string } | null | undefined) => {
  const message = String(error?.message || '').trim().toLowerCase();
  const code = String(error?.code || '').trim().toLowerCase();

  return code === INVALID_PASSWORD_CODE
    || (message.includes('password') && (message.includes('at least') || message.includes('uppercase') || message.includes('number') || message.includes('length') || message.includes('min')));
};

const createSignupError = (message: string, code: string) => {
  const nextError = new Error(message) as Error & { code?: string };
  nextError.code = code;
  return nextError;
};

const toIdentifierSignUpError = (error: { message?: string; status?: number; code?: string } | Error) => {
  if (isUsernameTakenError(error)) {
    return createSignupError(USERNAME_TAKEN_MESSAGE, USERNAME_TAKEN_CODE);
  }

  if (isEmailAlreadyExistsError(error)) {
    return createSignupError(EMAIL_ALREADY_EXISTS_MESSAGE, EMAIL_ALREADY_EXISTS_CODE);
  }

  if (isPasswordValidationError(error)) {
    return createSignupError(INVALID_PASSWORD_MESSAGE, INVALID_PASSWORD_CODE);
  }

  if (isUnexpectedSignupFailure(error)) {
    return createSignupError(GENERIC_IDENTIFIER_SIGN_UP_FAILURE_MESSAGE, UNEXPECTED_SIGNUP_FAILURE_CODE);
  }

  return createSignupError(GENERIC_IDENTIFIER_SIGN_UP_FAILURE_MESSAGE, UNEXPECTED_SIGNUP_FAILURE_CODE);
};

const buildDisplayNameOnlyMetadata = (displayName: string) => ({
  display_name: displayName,
});

const buildLocationLabel = (metadata: Record<string, unknown> | null | undefined) => {
  if (!metadata) return null;

  const read = (...keys: string[]) => {
    for (const key of keys) {
      const rawValue = metadata[key];
      const value = clean(typeof rawValue === 'string' ? rawValue : String(rawValue ?? ''));
      if (value) return value;
    }
    return '';
  };

  const explicitLocation = read('location', 'location_label', 'locationLabel');
  if (explicitLocation) return explicitLocation;

  const city = read('city', 'town', 'locality');
  const region = read('region', 'state', 'province');
  const country = read('country', 'country_name', 'nation');
  const parts = [city, region, country].filter(Boolean);

  if (parts.length >= 2) return parts.join(', ');
  if (parts.length === 1) return parts[0];
  return null;
};

const readStoredIdentity = (raw: string | null): AppIdentity | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const uid = String(parsed?.uid || '').trim();
    if (!uid) return null;
    return {
      uid,
      email: parsed?.email ? String(parsed.email) : null,
      displayName: parsed?.displayName ? String(parsed.displayName) : null,
      locationLabel: parsed?.locationLabel ? String(parsed.locationLabel) : null,
      createdAt: parsed?.createdAt ? String(parsed.createdAt) : null,
      lastLoginAt: parsed?.lastLoginAt ? String(parsed.lastLoginAt) : null,
      isAnonymous: Boolean(parsed?.isAnonymous),
    };
  } catch {
    return null;
  }
};

const readStoredSession = (): Session | null => {
  try {
    const raw = localStorage.getItem(SESSION_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session & { user?: { id?: string | null } | null };
    const accessToken = String(parsed?.access_token || '').trim();
    const refreshToken = String(parsed?.refresh_token || '').trim();
    const userId = String(parsed?.user?.id || '').trim();

    if (!accessToken || !refreshToken || !userId) {
      return null;
    }

    if (isSessionExpired(parsed as Session)) {
      localStorage.removeItem(SESSION_STATE_KEY);
      authDebugLog('stored_session_discarded', {
        reason: 'expired',
        userId,
        expiresAtMs: getSessionExpiryMs(parsed as Session),
      });
      return null;
    }

    return parsed as Session;
  } catch {
    return null;
  }
};

const storeSession = (session: Session | null) => {
  try {
    if (!session?.user || !String(session.access_token || '').trim() || !String(session.refresh_token || '').trim()) {
      localStorage.removeItem(SESSION_STATE_KEY);
      authDebugLog('session_store_clear', {
        reason: 'missing_session_user_or_tokens',
      });
      return;
    }

    localStorage.setItem(SESSION_STATE_KEY, JSON.stringify(session));
    authDebugLog('session_store_save', {
      userId: session.user?.id || null,
      userEmail: session.user?.email || null,
    });
  } catch {
    // ignore storage errors
  }
};

export function sessionToAppIdentity(session: Session | null): AppIdentity | null {
  const user = session?.user;
  if (!user) return null;
  const displayName = String(
    user.user_metadata?.display_name ||
      user.user_metadata?.full_name ||
      user.user_metadata?.name ||
      user.user_metadata?.preferred_name ||
      user.user_metadata?.username ||
      user.email ||
      ''
  ).trim();
  const locationLabel = buildLocationLabel(user.user_metadata as Record<string, unknown> | null | undefined);

  return {
    uid: user.id,
    email: user.email ?? null,
    displayName: displayName || null,
    ...(locationLabel ? { locationLabel } : {}),
    createdAt: String((user as { created_at?: string | null; createdAt?: string | null })?.created_at || (user as { created_at?: string | null; createdAt?: string | null })?.createdAt || '').trim() || null,
    lastLoginAt: String((user as { last_sign_in_at?: string | null; lastSignInAt?: string | null })?.last_sign_in_at || (user as { last_sign_in_at?: string | null; lastSignInAt?: string | null })?.lastSignInAt || '').trim() || null,
    isAnonymous: false,
  };
}

export function syncStoredIdentity(session: Session | null) {
  try {
    if (!session?.user) {
      localStorage.removeItem(SESSION_USER_KEY);
      authDebugLog('identity_store_clear', {});
      return;
    }
    const identity = sessionToAppIdentity(session);
    if (!identity) return;
    localStorage.setItem(SESSION_USER_KEY, JSON.stringify(identity));
    authDebugLog('identity_store_save', {
      userId: identity.uid,
      userEmail: identity.email,
    });
  } catch {
    // ignore storage errors
  }
}

export function getStoredAppIdentity(): AppIdentity | null {
  try {
    return readStoredIdentity(localStorage.getItem(SESSION_USER_KEY))
      || readStoredIdentity(localStorage.getItem(MOCK_USER_KEY))
      || (() => {
        const session = readStoredSession();
        return session ? sessionToAppIdentity(session) : null;
      })();
  } catch {
    return null;
  }
}

export function clearStoredAppIdentity() {
  try {
    localStorage.removeItem(SESSION_USER_KEY);
    localStorage.removeItem(MOCK_USER_KEY);
  } catch {
    // ignore storage errors
  }
}

function clearSupabaseSessionArtifacts() {
  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key) continue;
      if (key === SUPABASE_AUTH_STORAGE_KEY || key === SUPABASE_AUTH_USER_STORAGE_KEY || key.startsWith(`${SUPABASE_AUTH_STORAGE_KEY}-`)) {
        keysToRemove.push(key);
      }
    }

    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  } catch {
    // ignore storage errors
  }
}

export function setPostAuthRedirect(path: string) {
  try {
    const next = String(path || '/dashboard').trim() || '/dashboard';
    sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, next);
  } catch {
    // ignore storage errors
  }
}

export function getPostAuthRedirect() {
  try {
    return String(sessionStorage.getItem(POST_AUTH_REDIRECT_KEY) || '/dashboard').trim() || '/dashboard';
  } catch {
    return '/dashboard';
  }
}

export function clearPostAuthRedirect() {
  try {
    sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
  } catch {
    // ignore storage errors
  }
}

export function setLoginFlowActive() {
  try {
    sessionStorage.setItem(LOGIN_FLOW_ACTIVE_KEY, 'true');
  } catch {
    // ignore storage errors
  }
}

export function getLoginFlowActive() {
  try {
    return sessionStorage.getItem(LOGIN_FLOW_ACTIVE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function clearLoginFlowActive() {
  try {
    sessionStorage.removeItem(LOGIN_FLOW_ACTIVE_KEY);
  } catch {
    // ignore storage errors
  }
}

export type StoredLoginAuthError = {
  message: string;
  nonce: number;
};

export const sanitizeLoginAuthErrorMessage = (raw?: string | null) => {
  const message = String(raw || '').trim();
  if (!message) return message;
  const lower = message.toLowerCase();
  if (
    lower.includes('not assigned to the requested tenant') ||
    lower.includes('not active for the requested tenant') ||
    lower.includes('not authorized for') ||
    lower.includes('tenant_access_denied')
  ) {
    return 'Invalid email or password.';
  }
  return message;
};

export function setStoredLoginAuthError(message: string) {
  try {
    const safeMessage = sanitizeLoginAuthErrorMessage(message) || 'Access denied.';
    const payload: StoredLoginAuthError = {
      message: String(safeMessage).trim(),
      nonce: Date.now(),
    };
    sessionStorage.setItem(LOGIN_AUTH_ERROR_KEY, JSON.stringify(payload));
    return payload;
  } catch {
    return null;
  }
}

export function getStoredLoginAuthError(): StoredLoginAuthError | null {
  try {
    const raw = sessionStorage.getItem(LOGIN_AUTH_ERROR_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const message = String(parsed?.message || '').trim();
    if (!message) return null;
    return {
      message,
      nonce: Number(parsed?.nonce || Date.now()),
    };
  } catch {
    return null;
  }
}

export function clearStoredLoginAuthError() {
  try {
    sessionStorage.removeItem(LOGIN_AUTH_ERROR_KEY);
  } catch {
    // ignore storage errors
  }
}

export type StoredLoginSuccessMessage = {
  message: string;
  nonce: number;
};

export function setStoredLoginSuccessMessage(message: string) {
  try {
    const payload: StoredLoginSuccessMessage = {
      message: String(message || '').trim() || 'Email confirmed. You can now sign in.',
      nonce: Date.now(),
    };
    sessionStorage.setItem(LOGIN_SUCCESS_MESSAGE_KEY, JSON.stringify(payload));
    return payload;
  } catch {
    return null;
  }
}

export function getStoredLoginSuccessMessage(): StoredLoginSuccessMessage | null {
  try {
    const raw = sessionStorage.getItem(LOGIN_SUCCESS_MESSAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const message = String(parsed?.message || '').trim();
    if (!message) return null;
    return {
      message,
      nonce: Number(parsed?.nonce || Date.now()),
    };
  } catch {
    return null;
  }
}

export function clearStoredLoginSuccessMessage() {
  try {
    sessionStorage.removeItem(LOGIN_SUCCESS_MESSAGE_KEY);
  } catch {
    // ignore storage errors
  }
}

export function setLogoutTransitionPending() {
  try {
    sessionStorage.setItem(LOGOUT_TRANSITION_PENDING_KEY, 'true');
  } catch {
    // ignore storage errors
  }
}

export function getLogoutTransitionPending() {
  try {
    return sessionStorage.getItem(LOGOUT_TRANSITION_PENDING_KEY) === 'true';
  } catch {
    return false;
  }
}

export function clearLogoutTransitionPending() {
  try {
    sessionStorage.removeItem(LOGOUT_TRANSITION_PENDING_KEY);
  } catch {
    // ignore storage errors
  }
}

export async function getCurrentSession() {
  authDebugLog('get_current_session_start', {});
  const client = getSupabaseClient();
  if (!client) {
    const storedSession = readStoredSession();
    syncStoredIdentity(storedSession ?? null);
    authDebugLog('get_current_session_fallback', {
      hasSession: !!storedSession,
      userId: storedSession?.user?.id || null,
      userEmail: storedSession?.user?.email || null,
    });
    return storedSession;
  }

  const { data, error } = await client.auth.getSession();
  if (error) {
    console.warn('[SupabaseAuth] getSession failed', error);
    authDebugLog('get_current_session_error', {
      message: String(error?.message || error || '').trim(),
      status: (error as { status?: number } | undefined)?.status ?? null,
      code: (error as { code?: string } | undefined)?.code ?? null,
    });
    const bootstrapError = new Error('Unable to verify your session. Please try again.') as Error & {
      code?: string;
      status?: number;
    };
    bootstrapError.code = String((error as { code?: string } | undefined)?.code || '').trim()
      || 'auth_session_unavailable';
    const status = (error as { status?: number } | undefined)?.status;
    if (typeof status === 'number') {
      bootstrapError.status = status;
    }
    throw bootstrapError;
  }

  const session = data.session ?? null;
  storeSession(session);
  syncStoredIdentity(session);
  authDebugLog('get_current_session_result', {
    hasSession: !!session,
    userId: session?.user?.id || null,
    userEmail: session?.user?.email || null,
  });
  return session;
}

export async function getSessionForAuthenticatedRequest(options?: AuthenticatedRequestSessionOptions) {
  const reason = String(options?.reason || 'authenticated_request').trim() || 'authenticated_request';
  const refreshThresholdMs = typeof options?.refreshThresholdMs === 'number'
    ? Math.max(0, options.refreshThresholdMs)
    : AUTH_SESSION_REFRESH_SKEW_MS;

  authDebugLog('request_session_start', {
    reason,
    refreshThresholdMs,
  });

  const client = getSupabaseClient();
  if (!client) {
    const storedSession = readStoredSession();
    if (!storedSession?.user) {
      syncStoredIdentity(null);
      authDebugLog('request_session_missing', {
        reason,
        source: 'stored',
      });
      throw createAuthenticatedRequestSessionError('No authenticated session is available.', 'auth_session_missing');
    }

    syncStoredIdentity(storedSession);
    authDebugLog('request_session_result', {
      reason,
      source: 'stored',
      hasSession: true,
      userId: storedSession.user.id || null,
      userEmail: storedSession.user.email || null,
      expiresAtMs: getSessionExpiryMs(storedSession),
    });
    return storedSession;
  }

  const { data, error } = await client.auth.getSession();
  if (error) {
    authDebugLog('request_session_get_error', {
      reason,
      message: String(error?.message || error || '').trim(),
      status: (error as { status?: number } | undefined)?.status ?? null,
      code: (error as { code?: string } | undefined)?.code ?? null,
    });
    throw createAuthenticatedRequestSessionError(
      String(error?.message || 'Unable to verify your session.').trim() || 'Unable to verify your session.',
      String((error as { code?: string } | undefined)?.code || '').trim() || 'auth_session_unavailable',
      { status: (error as { status?: number } | undefined)?.status ?? null },
    );
  }

  let session = data.session ?? null;
  if (!session?.user) {
    storeSession(null);
    syncStoredIdentity(null);
    authDebugLog('request_session_missing', {
      reason,
      source: 'supabase',
    });
    throw createAuthenticatedRequestSessionError('No authenticated session is available.', 'auth_session_missing');
  }

  if (isSessionExpired(session, refreshThresholdMs)) {
    authDebugLog('request_session_refresh_start', {
      reason,
      userId: session.user.id || null,
      userEmail: session.user.email || null,
      expiresAtMs: getSessionExpiryMs(session),
    });

    const refreshResult = await client.auth.refreshSession();
    if (refreshResult.error) {
      storeSession(null);
      syncStoredIdentity(null);
      authDebugLog('request_session_refresh_error', {
        reason,
        message: String(refreshResult.error?.message || refreshResult.error || '').trim(),
        status: (refreshResult.error as { status?: number } | undefined)?.status ?? null,
        code: (refreshResult.error as { code?: string } | undefined)?.code ?? null,
      });
      throw createAuthenticatedRequestSessionError(
        AUTH_SESSION_EXPIRED_MESSAGE,
        'auth_session_expired',
        { status: (refreshResult.error as { status?: number } | undefined)?.status ?? 401 },
      );
    }

    session = refreshResult.data.session ?? null;
    if (!session?.user || isSessionExpired(session)) {
      storeSession(null);
      syncStoredIdentity(null);
      authDebugLog('request_session_refresh_missing', {
        reason,
        hasSession: !!session,
        expiresAtMs: getSessionExpiryMs(session),
      });
      throw createAuthenticatedRequestSessionError(AUTH_SESSION_EXPIRED_MESSAGE, 'auth_session_expired', { status: 401 });
    }
  }

  storeSession(session);
  syncStoredIdentity(session);
  authDebugLog('request_session_result', {
    reason,
    source: 'supabase',
    hasSession: true,
    userId: session.user.id || null,
    userEmail: session.user.email || null,
    expiresAtMs: getSessionExpiryMs(session),
  });
  return session;
}

export function onSessionChange(callback: (session: Session | null) => void) {
  const client = getSupabaseClient();
  if (!client) return null;

  const { data } = client.auth.onAuthStateChange((_event, session) => {
    storeSession(session);
    syncStoredIdentity(session);
    callback(session);
  });

  return () => data.subscription.unsubscribe();
}

export async function redeemWarriorPartnerInvitation(invite: string, accessToken: string) {
  const response = await fetch('/api/outreach/partner-access/redeem', {
    method: 'POST',
    headers: applyTenantRequestHeaders({ Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' }),
    body: JSON.stringify({ invite }),
  });
  const payload = await response.json().catch(() => ({})) as { detail?: string; error?: string };
  if (!response.ok) throw new Error(payload.detail || payload.error || 'Unable to activate your partner access.');
}

export async function redeemGenericPartnerInvitation(invite: string, accessToken: string) {
  const response = await fetch('/api/outreach/generic-partner-access/redeem', {
    method: 'POST',
    headers: applyTenantRequestHeaders({ Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' }),
    body: JSON.stringify({ invite }),
  });
  const payload = await response.json().catch(() => ({})) as { detail?: string; error?: string };
  if (!response.ok) throw new Error(payload.detail || payload.error || 'Unable to activate your partnership access.');
}

export async function exchangeCodeForSessionFromUrl() {
  authDebugLog('exchange_code_for_session_start', {});
  const client = getSupabaseClient();
  if (!client) {
    const storedSession = readStoredSession();
    syncStoredIdentity(storedSession ?? null);
    authDebugLog('exchange_code_for_session_fallback', {
      hasSession: !!storedSession,
      userId: storedSession?.user?.id || null,
      userEmail: storedSession?.user?.email || null,
    });
    return storedSession;
  }

  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  authDebugLog('exchange_code_for_session_url', {
    hasCode: !!code,
    codePrefix: code ? code.slice(0, 8) : null,
  });

  if (code) {
    const { data, error } = await client.auth.exchangeCodeForSession(code);
    if (error) {
      authDebugLog('exchange_code_for_session_error', {
        message: String(error?.message || error || '').trim(),
        status: (error as { status?: number } | undefined)?.status ?? null,
        code: (error as { code?: string } | undefined)?.code ?? null,
      });
      throw error;
    }
    const exchangedSession = data.session ?? null;
    authDebugLog('callback_exchange_success', {
      hasSession: !!exchangedSession,
      userId: exchangedSession?.user?.id || null,
      userEmail: exchangedSession?.user?.email || null,
    });
    const committedSession = exchangedSession?.access_token && exchangedSession?.refresh_token
      ? (await setSessionFromServerSession(exchangedSession, {
          reason: 'callback_exchange',
          requireAuthoritativeStorage: true,
        })).session
      : exchangedSession;

    storeSession(committedSession ?? null);
    syncStoredIdentity(committedSession ?? null);
    authDebugLog('exchange_code_for_session_result', {
      hasSession: !!committedSession,
      userId: committedSession?.user?.id || null,
      userEmail: committedSession?.user?.email || null,
      ...getAuthoritativeStoragePresence(),
    });
    return committedSession ?? null;
  }

  const { data, error } = await client.auth.getSession();
  if (error) {
    authDebugLog('exchange_code_for_session_get_session_error', {
      message: String(error?.message || error || '').trim(),
      status: (error as { status?: number } | undefined)?.status ?? null,
      code: (error as { code?: string } | undefined)?.code ?? null,
    });
    throw error;
  }
  storeSession(data.session ?? null);
  syncStoredIdentity(data.session ?? null);
  authDebugLog('exchange_code_for_session_result', {
    hasSession: !!data.session,
    userId: data.session?.user?.id || null,
    userEmail: data.session?.user?.email || null,
    usedFallbackSession: true,
  });
  return data.session ?? null;
}

export async function signInWithGoogle() {
  const client = getSupabaseClient();
  if (!client) {
    throw new Error('Supabase Auth is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY (or VITE_SUPABASE_PUBLISHABLE_KEY).');
  }

  const redirectTo = buildAppUrl('/auth/callback');
  authDebugLog('google_sign_in_start', {
    redirectTo,
  });

  const { data, error } = await client.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo,
      queryParams: {
        prompt: 'select_account',
      },
    },
  });

  if (error) {
    authDebugLog('google_sign_in_error', {
      message: String(error?.message || error || '').trim(),
      status: (error as { status?: number } | undefined)?.status ?? null,
      code: (error as { code?: string } | undefined)?.code ?? null,
    });
    throw error;
  }
  if (data?.url) {
    authDebugLog('google_sign_in_redirect', {
      url: data.url,
    });
    window.location.assign(data.url);
  }

  return data;
}

export async function signInWithEmail(email: string, password: string) {
  const client = getSupabaseClient();
  if (!client) {
    throw new Error('Supabase Auth is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY (or VITE_SUPABASE_PUBLISHABLE_KEY).');
  }

  const { data, error } = await client.auth.signInWithPassword({
    email: clean(email),
    password,
  });

  if (error) throw toIdentifierSignInError(error);
  syncStoredIdentity(data.session ?? null);
  return data;
}

async function setSessionFromServerSession(
  session: { access_token?: string; refresh_token?: string } | null | undefined,
  options?: SessionCommitOptions,
) {
  const client = getSupabaseClient();
  const reason = String(options?.reason || 'session_commit').trim() || 'session_commit';
  const requireAuthoritativeStorage = options?.requireAuthoritativeStorage === true;
  const accessToken = String(session?.access_token || '').trim();
  const refreshToken = String(session?.refresh_token || '').trim();
  authDebugLog('session_commit_start', {
    reason,
    hasClient: !!client,
    hasAccessToken: !!accessToken,
    hasRefreshToken: !!refreshToken,
  });
  if (!accessToken || !refreshToken) {
    throw new Error('Unable to sign in right now. Please try again.');
  }

  const nextSession = session as Session;

  if (!client) {
    storeSession(nextSession);
    syncStoredIdentity(nextSession);
    authDebugLog('session_commit_success', {
      reason,
      source: 'fallback',
      userId: nextSession?.user?.id || null,
      userEmail: nextSession?.user?.email || null,
      ...getAuthoritativeStoragePresence(),
    });
    return {
      session: nextSession,
      user: nextSession?.user ?? null,
    } as const;
  }

  try {
    const { data, error } = await client.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });

    if (error) throw error;

    const syncedSession = data.session ?? nextSession;
    // For a callback exchange, Supabase's own durable storage is the commit
    // point. Verify it before writing our compatibility session/identity keys:
    // otherwise a failed durable write can briefly authenticate App from a local
    // mirror and unmount the callback error surface.
    const storagePresence = getAuthoritativeStoragePresence();

    if (requireAuthoritativeStorage && !storagePresence.storageKeyPresent) {
      const storageError = createAuthenticatedRequestSessionError(
        'We could not finish signing you in. Please try again.',
        'auth_session_commit_failed',
        { status: 500 },
      );
      authDebugLog('session_commit_failure', {
        reason,
        failure: 'authoritative_storage_missing',
        userId: syncedSession?.user?.id || null,
        userEmail: syncedSession?.user?.email || null,
        ...storagePresence,
      });
      throw storageError;
    }

    storeSession(syncedSession);
    syncStoredIdentity(syncedSession);
    authDebugLog('session_commit_success', {
      reason,
      source: 'supabase',
      hasSession: !!syncedSession,
      userId: syncedSession?.user?.id || null,
      userEmail: syncedSession?.user?.email || null,
      ...storagePresence,
    });

    return {
      session: syncedSession,
      user: data.user ?? syncedSession?.user ?? null,
    } as const;
  } catch (error) {
    authDebugLog('session_commit_failure', {
      reason,
      message: String((error as Error)?.message || error || '').trim(),
      status: (error as { status?: number } | undefined)?.status ?? null,
      code: (error as { code?: string } | undefined)?.code ?? null,
      ...getAuthoritativeStoragePresence(),
    });
    throw error;
  }
}

export async function signInWithIdentifier(identifier: string, password: string) {
  const nextIdentifier = clean(identifier);
  if (isLikelyEmailAddress(nextIdentifier)) {
    return signInWithEmail(nextIdentifier, password);
  }

  const response = await fetch(IDENTIFIER_SIGN_IN_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      identifier: nextIdentifier,
      password,
    }),
  });

  const payload = await response.json().catch(() => ({})) as {
    error?: string;
    session?: {
      access_token?: string;
      refresh_token?: string;
    };
  };

  if (!response.ok) {
    throw toIdentifierSignInError({
      message: String(payload?.error || '').trim() || GENERIC_IDENTIFIER_SIGN_IN_FAILURE_MESSAGE,
      status: response.status,
    });
  }

  return setSessionFromServerSession(payload?.session);
}

export async function signUpWithEmail(email: string, password: string, displayName: string, emailRedirectOverride?: string, betaInviteToken?: string) {
  const nextDisplayName = normalizeDisplayName(displayName);
  const emailRedirectTo = clean(emailRedirectOverride) || buildAppUrl('/auth/callback?flow=email-confirmation');
  authDebugLog('signup_start', {
    emailRedirectTo,
  });

  try {
    const response = await fetch(IDENTIFIER_SIGN_UP_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: clean(email),
        password,
        displayName: nextDisplayName,
        emailRedirectTo,
        ...(clean(betaInviteToken) ? { betaInviteToken: clean(betaInviteToken) } : {}),
      }),
    });

    const payload = await response.json().catch(() => ({})) as {
      code?: string;
      error?: string;
      session?: {
        access_token?: string;
        refresh_token?: string;
      };
      user?: unknown;
    };

    if (!response.ok) {
      authDebugLog('signup_error', {
        status: response.status,
        code: payload?.code || null,
        message: String(payload?.error || '').trim() || GENERIC_IDENTIFIER_SIGN_UP_FAILURE_MESSAGE,
      });
      throw toIdentifierSignUpError({
        code: payload?.code,
        message: String(payload?.error || '').trim() || GENERIC_IDENTIFIER_SIGN_UP_FAILURE_MESSAGE,
        status: response.status,
      });
    }

    if (payload?.session?.access_token && payload?.session?.refresh_token) {
      const sessionResult = await setSessionFromServerSession(payload.session);
      authDebugLog('signup_session_result', {
        hasSession: !!sessionResult.session,
        userId: sessionResult.session?.user?.id || null,
        userEmail: sessionResult.session?.user?.email || null,
      });
      return {
        session: sessionResult.session,
        user: payload?.user ?? sessionResult.session?.user ?? null,
      } as const;
    }

    syncStoredIdentity(null);
    authDebugLog('signup_session_absent', {
      hasUser: !!payload?.user,
    });
    return {
      session: null,
      user: payload?.user ?? null,
    } as const;
  } catch (error) {
    throw toIdentifierSignUpError(error as { message?: string; status?: number; code?: string } | Error);
  }
}

export async function redeemBetaInvitation(token: string) {
  const session = await getCurrentSession();
  const accessToken = String(session?.access_token || '').trim();
  if (!accessToken) throw new Error('Sign in to redeem this beta invitation.');
  const response = await fetch('/api/beta/invitations/redeem', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token }),
  });
  const payload = await response.json().catch(() => ({})) as { error?: string; detail?: string; granted?: boolean };
  if (!response.ok || payload.granted !== true) throw new Error(String(payload.detail || payload.error || 'Unable to redeem this beta invitation.'));
  return payload;
}

export async function checkUsernameAvailability(username: string) {
  const nextUsername = clean(username);
  if (!nextUsername) {
    return { available: false } as const;
  }

  const response = await fetch(`${USERNAME_AVAILABILITY_ENDPOINT}?username=${encodeURIComponent(nextUsername)}`, {
    headers: {
      Accept: 'application/json',
    },
  });

  const payload = await response.json().catch(() => ({})) as {
    available?: boolean;
    error?: string;
  };

  if (!response.ok) {
    throw new Error(String(payload?.error || 'Unable to check username availability right now.').trim() || 'Unable to check username availability right now.');
  }

  return {
    available: Boolean(payload?.available),
  } as const;
}

export async function updateCurrentUserDisplayName(displayName: string) {
  const nextDisplayName = normalizeDisplayName(displayName);
  if (!nextDisplayName) return null;

  const client = getSupabaseClient();
  if (!client) {
    const session = await getCurrentSession();
    if (!session?.user) {
      return null;
    }

    const existingMetadata = session.user.user_metadata && typeof session.user.user_metadata === 'object'
      ? session.user.user_metadata
      : {};

    const updatedSession = {
      ...session,
      user: {
        ...session.user,
        user_metadata: {
          ...existingMetadata,
          ...buildDisplayNameOnlyMetadata(nextDisplayName),
        },
      },
    } as Session;

    storeSession(updatedSession);
    syncStoredIdentity(updatedSession);
    return null;
  }

  const session = await getCurrentSession();
  const existingMetadata = session?.user?.user_metadata && typeof session.user.user_metadata === 'object'
    ? session.user.user_metadata
    : {};

  const performUpdate = async () => client.auth.updateUser({
    data: {
      ...existingMetadata,
      ...buildDisplayNameOnlyMetadata(nextDisplayName),
    },
  });

  const applySuccessfulUpdate = async (data: { user?: Session['user'] | null } | null | undefined) => {
    syncStoredIdentity(data?.user ? await getCurrentSession() : null);
    return data;
  };

  try {
    const { data, error } = await performUpdate();
    if (error) throw error;
    return applySuccessfulUpdate(data);
  } catch (error) {
    if (!isExpiredJwtError(error)) {
      throw error;
    }

    const refreshResult = await client.auth.refreshSession();
    if (refreshResult.error || !refreshResult.data?.session?.user) {
      storeSession(null);
      syncStoredIdentity(null);
      throw createAuthenticatedRequestSessionError(AUTH_SESSION_EXPIRED_MESSAGE, 'auth_session_expired', {
        status: (refreshResult.error as { status?: number } | undefined)?.status ?? 401,
      });
    }

    const retryResult = await performUpdate();
    if (retryResult.error) {
      if (isExpiredJwtError(retryResult.error)) {
        storeSession(null);
        syncStoredIdentity(null);
        throw createAuthenticatedRequestSessionError(AUTH_SESSION_EXPIRED_MESSAGE, 'auth_session_expired', { status: 401 });
      }

      throw retryResult.error;
    }

    return applySuccessfulUpdate(retryResult.data);
  }
}

function clearLocalAuthArtifacts() {
  clearSupabaseSessionArtifacts();
  clearStoredAppIdentity();
  storeSession(null);
  clearPostAuthRedirect();
  clearLoginFlowActive();
  clearStoredLoginAuthError();
  clearStoredLoginSuccessMessage();
  try {
    sessionStorage.removeItem(SIGNUP_CHECKOUT_HANDOFF_KEY);
    sessionStorage.removeItem(FIRST_SESSION_ONBOARDING_REQUESTED_KEY);
    sessionStorage.removeItem(JOURNAL_DATE_RANGE_STORAGE_KEY);
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(WIDGET_TIMEFRAME_STORAGE_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // ignore storage errors
  }
}

type SignOutOptions = {
  scope?: 'global' | 'local' | 'others';
};

export async function signOut(options?: SignOutOptions) {
  const client = getSupabaseClient();
  authDebugLog('sign_out_start', {
    scope: options?.scope || 'default',
  });
  clearLocalAuthArtifacts();
  try {
    if (client) {
      const signOutArgs = options?.scope ? { scope: options.scope } : undefined;
      const { error } = await client.auth.signOut(signOutArgs as any);
      if (error) {
        authDebugLog('sign_out_error', {
          message: String(error?.message || error || '').trim(),
          status: (error as { status?: number } | undefined)?.status ?? null,
          code: (error as { code?: string } | undefined)?.code ?? null,
        });
        throw error;
      }
    }
  } finally {
    clearLocalAuthArtifacts();
    authDebugLog('sign_out_complete', {
      scope: options?.scope || 'default',
    });
  }
}

export async function sendPasswordResetEmail(email: string) {
  const client = getSupabaseClient();
  if (!client) {
    throw new Error('Supabase Auth is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY (or VITE_SUPABASE_PUBLISHABLE_KEY).');
  }

  const { data, error } = await client.auth.resetPasswordForEmail(clean(email), {
    redirectTo: buildAppUrl('/auth/reset-password'),
  });

  if (error) throw error;
  return data;
}

export async function updatePassword(password: string) {
  const client = getSupabaseClient();
  if (!client) {
    throw new Error('Supabase Auth is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY (or VITE_SUPABASE_PUBLISHABLE_KEY).');
  }

  const { data, error } = await client.auth.updateUser({ password });
  if (error) throw error;
  return data;
}

export { isSupabaseConfigured } from './supabase';
